'use client'

import React, { useState } from 'react';
import { Avatar as MantineAvatar, MantineRadius, Skeleton } from '@mantine/core';
import { publicImageUrl } from '@/lib/staticAssets';

// Global cache for loaded avatar images - prevents re-showing skeleton on re-mount
const loadedAvatars = new Set<string>();

interface AvatarProps {
  userId: string;
  avatarId: string | null;
  size?: number;
  className?: string;
  alt?: string;
  placeholder?: React.ReactNode;
  radius?: MantineRadius;
  loading?: 'lazy' | 'eager';
  priority?: boolean; // For LCP images
}

export function Avatar({
  userId,
  avatarId,
  size = 80,
  className,
  radius, // Ignored - always use perfect circle
  alt = 'Avatar',
  placeholder,
  loading = 'lazy',
  priority = false,
}: AvatarProps) {
  const cacheKey = `${userId}:${avatarId}`;
  const [imageLoaded, setImageLoaded] = useState(() => loadedAvatars.has(cacheKey));
  const finalAvatarId = avatarId || 'default';
  const defaultAvatar = publicImageUrl('images/default-avatar.svg');

  let src = defaultAvatar;
  if (finalAvatarId !== 'default') {
    src = `/api/files/avatar?userId=${encodeURIComponent(userId)}&avatarId=${encodeURIComponent(finalAvatarId)}&w=${size}&h=${size}`;
  }

  // Show skeleton for non-default avatars until loaded
  const showSkeleton = finalAvatarId !== 'default' && !imageLoaded;

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      {showSkeleton && (
        <Skeleton
          circle
          width={size}
          height={size}
          animate={false}
          className={className}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 1,
            opacity: 0.3,
            backgroundColor: '#e0e0e0',
          }}
        />
      )}
      <MantineAvatar
        src={src}
        alt={alt}
        radius={9999}
        size={size}
        className={className}
        styles={{
          image: { 
            objectFit: 'cover',
            width: '100%',
            height: '100%',
            opacity: imageLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
            borderRadius: '50%',
          },
          placeholder: { 
            backgroundColor: 'transparent',
            borderRadius: '50%',
          },
          root: { 
            backgroundColor: 'transparent',
            aspectRatio: '1 / 1',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
          },
        }}
        imageProps={{
          loading,
          fetchPriority: priority ? 'high' : 'auto',
          decoding: 'async',
          onLoad: () => {
            loadedAvatars.add(cacheKey);
            setImageLoaded(true);
          },
          onError: (e: React.SyntheticEvent<HTMLImageElement>) => {
            // Fallback to default avatar on error
            if (e.currentTarget.src !== defaultAvatar) {
              e.currentTarget.src = defaultAvatar;
            }
            setImageLoaded(true);
          },
        } as React.ImgHTMLAttributes<HTMLImageElement>}
      >
        {placeholder}
      </MantineAvatar>
    </div>
  );
}
